import React, { useEffect, useState } from 'react'

import { CreateStyleboardModal, FadeIn, IntersectionVisible, BackToTop } from '@pb/components'
import { getLabelFromStorage, isHmBrand, trackViewEvent } from '@pb/utils'
import { useSelector, useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'

import { STYLEBOARD_ROUTES } from 'App/constants'
import appSelectors from 'App/duck/selectors'
import { EVENT_TYPES, PAGE_ID, CATEGORY_PATH, getTabTracking } from 'features/favourites/tracking'
import StyleboardNotification from 'features/Styleboard/styleboard-overview/components/StyleboardNotification'
import { operationFetchStyleboards } from 'features/Styleboard/styleboard-overview/duck/operations'
import styleboardSelectors from 'features/Styleboard/styleboard-overview/duck/selectors'
import { NotificationType } from 'features/Styleboard/types'

import NonLoggedIn from '../Following/components/NonLoggedIn'
import StyleboardsError from '../StyleboardsError'
import Loader from './components/Loader'
import NoStyleboards from './components/NoStyleboards'
import Styleboards from './components/Styleboards'
import { AddedStyleboards, MyStyleboardsProps } from './types'

const MyStyleboards: React.FC<MyStyleboardsProps> = ({
  titleSize = 'large',
  errorVariation = 'notification',
  shouldSendTrackingOnLoad = false,
}) => {
  const dispatch = useDispatch()
  const history = useHistory()

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [addedStyleboards, setAddedStyleboards] = useState<AddedStyleboards[] | string | undefined>(
    [],
  )
  const isValidatedUser = useSelector(appSelectors.getValidatedUser)
  const isSignedIn = useSelector(appSelectors.getIsSignedIn)

  const myStyleboards = useSelector(styleboardSelectors.getMyStyleboards)
  const nextItemsLink = useSelector(styleboardSelectors.getMyStyleboardsNextItemsLink)
  const isMyStyleboardsLoading = useSelector(styleboardSelectors.getIsMyStyleboardsLoading)
  const isMyStyleboardsLoadingFailed = useSelector(
    styleboardSelectors.getIsMyStyleboardLoadingFailed,
  )

  const redirectToCreatedStyleboard = (
    styleboards: AddedStyleboards[] | string | undefined,
  ): void => {
    const oneAddedStyleboard = styleboards && styleboards?.length === 1

    if (oneAddedStyleboard) {
      const styleboardId = (styleboards as AddedStyleboards[])[0].id
      history.push(STYLEBOARD_ROUTES.Styleboard.path.replace(/:styleboardId/, styleboardId))
    }
  }

  const handleAddedStyleboard = (styleboards: AddedStyleboards[] | string | undefined): void => {
    setAddedStyleboards(styleboards)
    redirectToCreatedStyleboard(styleboards)
  }

  const handleLoadMoreStyleboards = (): void => {
    if (nextItemsLink) {
      dispatch(operationFetchStyleboards(nextItemsLink))
    }
  }

  useEffect(() => {
    if (shouldSendTrackingOnLoad) {
      trackViewEvent(
        getTabTracking(
          EVENT_TYPES.my_styleboards_view,
          PAGE_ID.my_styleboards,
          CATEGORY_PATH.my_styleboards,
        ),
        true,
        isHmBrand,
      )
    }
  }, [])

  useEffect(() => {
    if (isSignedIn) {
      dispatch(operationFetchStyleboards())
    }
  }, [isSignedIn])

  useEffect(() => {
    if (addedStyleboards && addedStyleboards.length) {
      dispatch(operationFetchStyleboards())
    }
  }, [addedStyleboards])

  if (!isSignedIn) {
    return <NonLoggedIn />
  }

  const createStyleboardModal = (
    <CreateStyleboardModal
      shouldShowCreatedNotification={false}
      isValidatedUser={isValidatedUser}
      toggleCreateModal={() => setIsCreateModalOpen(!isCreateModalOpen)}
      isCreateModalOpen={isCreateModalOpen}
      setAddedStyleboards={(styleboards) => handleAddedStyleboard(styleboards)}
    />
  )

  if (isMyStyleboardsLoadingFailed)
    return errorVariation === 'messageAndNotification' ? (
      <StyleboardsError titleSize={titleSize} />
    ) : (
      <StyleboardNotification includeNotifications={[NotificationType.FETCH_STYLEBOARDS_FAILED]} />
    )

  if (myStyleboards && myStyleboards.styleboards.length) {
    return (
      <FadeIn>
        <Styleboards
          styleboards={myStyleboards.styleboards}
          setIsCreateModalOpen={setIsCreateModalOpen}
        />
        <IntersectionVisible onShow={handleLoadMoreStyleboards} active />
        {createStyleboardModal}
        <BackToTop label={getLabelFromStorage('back.to.top.button')} />
      </FadeIn>
    )
  }

  return isMyStyleboardsLoading ? (
    <Loader />
  ) : (
    <NoStyleboards
      createStyleboardModal={createStyleboardModal}
      onClick={() => setIsCreateModalOpen(!isCreateModalOpen)}
    />
  )
}

export default MyStyleboards
